import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Account } from '@/domain/accounts/account'
import { Agreement, AgreementStatus, UpdateAgreementData } from '@/domain/agreements/agreement'
import { AssuredPerson, AssuredPersonRelationship, AssuredPersonStatus, formatAssuredPersonRelationship, formatAssuredPersonStatus, getAssuredPersonStatusColor } from '@/domain/assured/Assured'
import { Offering } from '@/domain/offerings/offering'
import { formatRedemptionStatusColor, formatRedemptionStatusForBadge, formatRedemptionType } from '@/domain/redeem/redeem'
import { DocType } from "@/domain/shared"
import { useAssuredPeopleByContract, useCreateAssuredPerson, useDeleteAssuredPerson, useUpdateAssuredPerson } from '@/hooks/api/useAssuredPeople'
import { useDeleteRedemption, useRedemptionsByContract } from '@/hooks/api/useRedemptions'
import { Paths } from '@/routes/paths'
import { calculateAgeStr } from '@/utils/dates'
import { formatDocNumber } from '@/utils/helpers'
import { dateOfBirthSchema } from '@/utils/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { Calendar, ChevronDown, ChevronUp, IdCardIcon, Info, Pencil, Plus, Trash2, User, Users } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import DateSelector from '../../shared/DateSelector'
import { AccountStatusCard } from './main-components/AccountStatusCard'
import { AgreementDetailsHeader } from './main-components/AgreementDetailsHeader'
import { AgreementPaymentsInfo } from './AgreementPaymentsInfo'
import { DocumentsCard } from './documents/DocumentsCard'

export interface AssuredCmpProps {
  agreement: Agreement
  offering?: Offering
  contractAccount?: Account
  userAccounts?: Account[]
  totalBalance: number
  isLoadingAccounts: boolean
  onViewAccountMovements: () => void
  isEditing?: boolean
  isSaving?: boolean
  onSave?: (changes: UpdateAgreementData) => void
  onCancel?: () => void
  onEdit?: () => void
}

export const AssuredCmp = (props: AssuredCmpProps) => {
  const {
    agreement, offering, contractAccount, userAccounts, totalBalance, isLoadingAccounts,
    onViewAccountMovements, isEditing, isSaving, onSave, onCancel, onEdit
  } = props

  const { data: assuredPeople } = useAssuredPeopleByContract(agreement.id)

  return (

    <div className="flex flex-col gap-4">
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <AgreementDetailsHeader
            agreement={agreement}
            offering={offering}
            totalBalance={totalBalance}
            actions={{
              isEditing,
              isSaving,
              onSave,
              onCancel,
              onEdit,
            }}
            data={{
              userAccounts,
              contractAccount,
              isLoadingAccounts,
            }}
          />
          <CardContent className="space-y-6">
            <AssuredPeopleList agreement={agreement} offering={offering} assuredPeople={assuredPeople} />
            <RedemptionsList agreement={agreement} assuredPeople={assuredPeople} />
          </CardContent>
        </Card>

        <div className="space-y-4">
          {contractAccount && (
            <AccountStatusCard
              contractAccount={contractAccount}
              onViewMovements={onViewAccountMovements}
            />
          )}
          <DocumentsCard agreementId={agreement.id} />
        </div>
      </div>
      <AgreementPaymentsInfo
        agreement={agreement}
        serviceType={offering?.serviceType}
      />
    </div>
  )
}

interface RedemptionsListProps {
  agreement: Agreement
  assuredPeople?: AssuredPerson[]
}

const RedemptionsList = ({ agreement, assuredPeople }: RedemptionsListProps) => {
  const { data: redemptions } = useRedemptionsByContract(agreement.id)
  const { mutate: deleteRedemption, isPending: isDeleting } = useDeleteRedemption()
  const navigate = useNavigate()
  const [showRedemptions, setShowRedemptions] = useState(false)

  if (!redemptions || redemptions.length === 0) return null

  const handleDelete = (id: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta redención?')) {
      deleteRedemption(id)
    }
  }

  return (
    <div className="flex flex-col gap-4 pt-4 border-t border-border">
      <Button
        variant="ghost"
        className="flex-1 flex items-center justify-between p-4 h-auto hover:bg-muted/50 rounded-xl border border-dashed border-border/60 transition-all group"
        onClick={() => setShowRedemptions(!showRedemptions)}
      >
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
            <IdCardIcon className="h-4 w-4 text-warning flex-shrink-0" />
          </div>
          <div className="flex flex-col items-start text-left">
            <span className="font-bold text-sm">Redenciones Aplicadas</span>
            <span className="text-xs text-muted-foreground">{redemptions.length} redención{redemptions.length !== 1 ? 'es' : ''}</span>
          </div>
        </div>
        {showRedemptions ? (
          <ChevronUp className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        )}
      </Button>

      {showRedemptions && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300 grid gap-3">
          {redemptions.map(r => (
            <div key={r.id} className="p-4 border rounded-xl flex flex-col gap-2 bg-card">
              <div className="flex flex-col justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{formatRedemptionType(r.type)}</div>
                  <Badge variant={"outline"} className={formatRedemptionStatusColor(r.status)}>{formatRedemptionStatusForBadge(r.status)}</Badge>
                </div>
                <div className='flex flex-col gap-1'>
                  {r.ids?.map(id => assuredPeople?.find(a => a.id === id)?.name).map((name, i) => (
                    <span key={i} className='text-sm'>{name}</span>
                  ))}
                </div>
              </div>
              <div className='flex justify-between items-center'>
                {r.redeemedContractNumber && <Badge variant="outline" className='text-sm'>Contrato {r.redeemedContractNumber}</Badge>}
                <div className="flex items-center gap-2 ml-auto">
                  {r.status === 'pending' && !r.redeemedContractId && (
                    <Button
                      variant="info"
                      size="sm"
                      className="max-w-xs"
                      onClick={() => navigate({ to: `${Paths.Management.Contracts}/${agreement.id}/redencion/${r.id}` })}
                    >
                      Completar redención
                    </Button>
                  )}
                  {r.redeemedContractId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="max-w-xs"
                      onClick={() => navigate({ to: `${Paths.Management.Contracts}/${r.redeemedContractId}` })}
                    >
                      Ir al contrato
                    </Button>
                  )}
                  <IconButton
                    variant="danger"
                    onClick={() => handleDelete(r.id)}
                    disabled={isDeleting}
                    title="Eliminar redención"
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface AssuredPeopleListProps {
  agreement: Agreement
  offering?: Offering
  assuredPeople?: AssuredPerson[]
}

export const AssuredPeopleList = ({ agreement, offering, assuredPeople }: AssuredPeopleListProps) => {
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAssuredPeople, setShowAssuredPeople] = useState(false)
  const canModify = (agreement.status === AgreementStatus.Active || agreement.status === AgreementStatus.Pending)

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            className="flex-1 flex items-center justify-between p-4 h-auto hover:bg-muted/50 rounded-xl border border-dashed border-border/60 transition-all group"
            onClick={() => setShowAssuredPeople(!showAssuredPeople)}
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
                <Users className="h-4 w-4 text-primary flex-shrink-0" />
              </div>
              <div className="flex flex-col items-start text-left">
                <span className="font-bold text-sm">Personas en Abono</span>
                <span className="text-xs text-muted-foreground">{assuredPeople?.length || 0} persona{(assuredPeople?.length || 0) !== 1 ? 's' : ''} registrada{(assuredPeople?.length || 0) !== 1 ? 's' : ''}</span>
              </div>
            </div>
            {showAssuredPeople ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>

          {canModify && offering?.hasMultipleOffered && (
            <Button
              size="sm"
              variant="info"
              className="h-full py-2 px-3 rounded-xl shadow-sm hover:shadow-md transition-all shrink-0"
              onClick={() => {
                setIsAdding(true)
                setShowAssuredPeople(true)
              }}
              disabled={isAdding}
            >
              <Plus className="h-5 w-5" />
              <span className="font-bold">Agregar familiar</span>
            </Button>
          )}
        </div>

        {showAssuredPeople && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-4">
            {isAdding && (
              <NewAssuredPersonCard
                agreementId={agreement.id}
                onClose={() => setIsAdding(false)}
              />
            )}

            {assuredPeople?.sort((a, b) => (a.dateOfBirth?.getTime() || 0) - (b.dateOfBirth?.getTime() || 0)).map((person) => (
              <AssuredPersonCard
                key={person.id}
                person={person}
                isEditing={editingId === person.id}
                onEdit={() => setEditingId(person.id || null)}
                onCancelEdit={() => setEditingId(null)}
                canModify={canModify}
                canDelete={(assuredPeople?.length || 0) > 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const assuredPersonSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  relationship: z.enum(AssuredPersonRelationship),
  docType: z.enum(DocType),
  docNumber: z.string(),
  dateOfBirth: dateOfBirthSchema,
  status: z.enum(AssuredPersonStatus).optional(),
  notes: z.string().optional()
})

type AssuredPersonFormValues = z.infer<typeof assuredPersonSchema>

const NewAssuredPersonCard = ({ agreementId, onClose }: { agreementId: string, onClose: () => void }) => {
  const { mutate: createAssuredPerson } = useCreateAssuredPerson()

  const form = useForm<AssuredPersonFormValues>({
    resolver: zodResolver(assuredPersonSchema),
    defaultValues: {
      name: '',
      relationship: undefined,
      docType: undefined,
      docNumber: '',
      dateOfBirth: new Date(),
      status: AssuredPersonStatus.ACTIVE,
      notes: ''
    }
  })

  const onSubmit = (data: AssuredPersonFormValues) => {
    createAssuredPerson({
      contractId: agreementId,
      name: data.name,
      relationship: data.relationship || undefined,
      docType: data.docType || undefined,
      docNumber: data.docNumber || '',
      dateOfBirth: data.dateOfBirth || new Date(),
      notes: data.notes || '',
      status: data.status || AssuredPersonStatus.ACTIVE
    }, {
      onSuccess: () => onClose()
    })
  }

  const handleDocNumberChange = (value: string) => {
    const docType = form.getValues("docType") as DocType || undefined
    form.setValue('docNumber', formatDocNumber(value, docType))
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="border border-primary rounded-md p-4 flex flex-col gap-3 relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Apellido, Nombre</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="relationship"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Parentesco</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar parentesco" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(AssuredPersonRelationship).map((rel) => (
                      <SelectItem key={rel} value={rel}>
                        {formatAssuredPersonRelationship(rel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="docType"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Tipo de Documento</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo de documento" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(DocType).map((docType) => (
                      <SelectItem key={docType} value={docType}>
                        {docType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="docNumber"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Número de Documento</FormLabel>
                <FormControl>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-md ${form.formState.errors.docNumber ? 'border-red-500' : ''}`}
                    value={field.value || ''}
                    onChange={(e) => handleDocNumberChange(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Nacimiento</FormLabel>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <DateSelector
                      initialDate={field.value}
                      onDateSelected={(date: Date) => {
                        field.onChange(date)
                      }}
                    />
                  </FormControl>
                  <span className="text-md font-mono font-bold text-gray-600 whitespace-nowrap">
                    ({form.watch('dateOfBirth') && calculateAgeStr(form.watch('dateOfBirth'))})
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Estado</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar estado" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(AssuredPersonStatus).map((status) => (
                      <SelectItem key={status} value={status}>
                        {formatAssuredPersonStatus(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="space-y-1 sm:col-span-2">
                <FormLabel>Notas</FormLabel>
                <FormControl>
                  <Input placeholder="Notas" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-between gap-2 mt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="info" type="submit" disabled={!form.watch('name')}>
            <Plus className="h-4 w-4" />
            Agregar Abonado
          </Button>
        </div>
      </form>
    </Form>
  )
}

const AssuredPersonCard = ({ person, isEditing, onEdit, onCancelEdit, canModify, canDelete }: { person: AssuredPerson, isEditing: boolean, onEdit: () => void, onCancelEdit: () => void, canModify: boolean, canDelete: boolean }) => {
  const { mutate: updateAssuredPerson } = useUpdateAssuredPerson()
  const { mutate: deleteAssuredPerson, isPending: isDeleting } = useDeleteAssuredPerson()

  const form = useForm<AssuredPersonFormValues>({
    resolver: zodResolver(assuredPersonSchema),
    defaultValues: {
      name: person.name,
      relationship: person.relationship || AssuredPersonRelationship.TITULAR,
      docType: person.docType || DocType.DNI,
      docNumber: person.docNumber || '',
      dateOfBirth: person.dateOfBirth || new Date(),
      status: person.status || AssuredPersonStatus.ACTIVE,
      notes: person.notes || ''
    }
  })

  const onSubmit = (data: AssuredPersonFormValues) => {
    if (!person.id) return;
    updateAssuredPerson({
      id: person.id,
      data: {
        name: data.name,
        relationship: data.relationship || undefined,
        docType: data.docType || undefined,
        docNumber: data.docNumber || '',
        dateOfBirth: data.dateOfBirth,
        status: data.status || AssuredPersonStatus.ACTIVE,
        notes: data.notes || ''
      }
    }, {
      onSuccess: () => onCancelEdit()
    })
  }

  const handleDelete = () => {
    if (!person.id) return;
    if (window.confirm('¿Está seguro de que desea eliminar este abonado?')) {
      deleteAssuredPerson(person.id)
    }
  }

  const handleDocNumberChange = (value: string) => {
    const docType = form.getValues("docType") as DocType || undefined
    form.setValue('docNumber', formatDocNumber(value, docType))
  }

  if (isEditing) {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="border rounded-md p-4 flex flex-col gap-3 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Apellido, Nombre</FormLabel>
                  <FormControl>
                    <Input placeholder="Apellido, Nombre" {...field} autoComplete='false' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="relationship"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Parentesco</FormLabel>
                  <Select onValueChange={(value) => field.onChange(value)} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar parentesco" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(AssuredPersonRelationship).map((rel) => (
                        <SelectItem key={rel} value={rel}>
                          {formatAssuredPersonRelationship(rel)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="docType"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Tipo de Documento</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo de documento" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(DocType).map((docType) => (
                        <SelectItem key={docType} value={docType}>
                          {docType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="docNumber"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Número de Documento</FormLabel>
                  <FormControl>
                    <input
                      type="text"
                      className={`w-full p-2 border rounded-md ${form.formState.errors.docNumber ? 'border-red-500' : ''}`}
                      value={field.value || ''}
                      onChange={(e) => handleDocNumberChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Nacimiento</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <DateSelector
                        initialDate={field.value}
                        onDateSelected={(date: Date) => {
                          field.onChange(date)
                        }}
                      />
                    </FormControl>
                    <span className="text-md font-mono font-bold text-gray-600 whitespace-nowrap">
                      ({calculateAgeStr(form.watch('dateOfBirth'))})
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Estado</FormLabel>
                  <Select onValueChange={(value) => field.onChange(value)} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(AssuredPersonStatus).filter((status) => status !== AssuredPersonStatus.SUSPENDED)
                        .map((status) => (
                          <SelectItem key={status} value={status}>
                            {formatAssuredPersonStatus(status)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem className="space-y-1 sm:col-span-2">
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Input placeholder="Notas" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-between gap-2 mt-2">
            <Button type="button" variant="outline" onClick={onCancelEdit}>Cancelar</Button>
            <Button type="submit" variant="warning" disabled={!form.watch('name')}>Guardar cambios</Button>
          </div>
        </form>
      </Form>
    )
  }

  const wasRemoved = person.status === AssuredPersonStatus.REMOVED

  return (
    <div className="group relative flex flex-col gap-4 p-5 rounded-2xl border bg-card hover:shadow-md transition-all duration-300">
      {/* Header Info */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-full border border-primary/20">
            <User className="h-5 w-5 text-primary flex-shrink-0" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold capitalize leading-tight">{person.name.toLowerCase()}</h4>
              {person.status && (
                <Badge className={`${getAssuredPersonStatusColor(person.status)} py-0 px-2 h-5`}>
                  {formatAssuredPersonStatus(person.status)}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-sm mt-0.5">
              {person.docType && person.docNumber && (
                <div className="flex items-center gap-1">
                  <IdCardIcon className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                  <span>{person.docType} {formatDocNumber(person.docNumber, person.docType as any)}</span>
                </div>
              )}
              {person.dateOfBirth && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                  <span>{calculateAgeStr(new Date(person.dateOfBirth), new Date())}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {canModify && (
          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={onEdit}
              title="Editar abonado"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
              onClick={handleDelete}
              disabled={isDeleting || !canDelete}
              title={canDelete ? "Eliminar abonado" : "No es posible eliminar el último abonado"}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-muted/20 border border-border/40">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Parentesco</span>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary/60 flex-shrink-0" />
            <span className="text-sm font-medium">
              {person.relationship ? formatAssuredPersonRelationship(person.relationship) : '-'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Fecha de Nacimiento</span>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
            <span className="text-sm font-medium">
              {person.dateOfBirth ? format(new Date(person.dateOfBirth), "dd/MM/yyyy") : '-'}
            </span>
          </div>
        </div>

        {wasRemoved && person.removedAt && (
          <div className="flex flex-col gap-1 sm:col-span-2 border-t pt-3 mt-1 border-border/40">
            <span className="text-[10px] font-bold text-destructive uppercase tracking-wider">Fecha de Baja</span>
            <div className="flex items-center gap-2 text-destructive">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium">{format(new Date(person.removedAt), "dd/MM/yyyy")}</span>
            </div>
          </div>
        )}
      </div>

      {/* Notes Section */}
      {person.notes && (
        <div className="px-1">
          <div className="flex items-start gap-2 text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 opacity-70" />
            <p className="text-xs leading-relaxed italic">
              {person.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
